import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApp } from "@/context/AppContext";
import { api, fmt, monthLabel, shiftMonth, currentMonth } from "@/lib/api";
import { getCategoryIcon } from "@/lib/icons";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { UserSettingsDialog } from "@/components/UserSettingsDialog";
import { TransactionModal } from "@/components/TransactionModal";
import { toast } from "sonner";
import { LayoutDashboard, Receipt, TrendingUp, ChevronLeft, ChevronRight, History, ArrowRightLeft, RefreshCw, Menu, Plus } from "lucide-react";

const overviewNav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/transactions", label: "Transactions", icon: Receipt, testid: "nav-transactions" },
  { to: "/trends", label: "Trends", icon: TrendingUp, testid: "trends-tab-button" },
];

const SidebarContent = ({ onNavigate }) => {
  const navigate = useNavigate();
  const { month, users, activeUser, switchUser } = useApp();
  const { data: dash } = useQuery({ queryKey: ["dashboard", month], queryFn: () => api.getDashboard(month) });
  const cats = dash?.categories || [];

  const goToCategory = (id) => {
    navigate("/");
    onNavigate?.();
    setTimeout(() => {
      const el = document.getElementById(`cat-card-${id}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-[#f97316]");
        setTimeout(() => el.classList.remove("ring-2", "ring-[#f97316]"), 1600);
      }
    }, 120);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2.5 px-5 h-16 shrink-0 border-b border-[#1c2537]">
        <span className="w-2.5 h-2.5 rounded-full bg-[#f97316] shadow-[0_0_10px_#f97316]" />
        <span className="font-heading font-extrabold text-lg tracking-[0.15em]">KRISTINA</span>
      </div>

      <div className="flex-1 overflow-y-auto py-5 px-3 space-y-6">
        <div>
          <p className="px-3 mb-2 text-[10px] font-semibold tracking-[0.18em] text-slate-500">OVERVIEW</p>
          <div className="space-y-0.5">
            {overviewNav.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onNavigate}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive ? "bg-[#f97316]/15 text-[#f97316]" : "text-slate-400 hover:text-slate-100 hover:bg-white/5"
                    }`
                  }
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </NavLink>
              );
            })}
          </div>
        </div>

        <div>
          <p className="px-3 mb-2 text-[10px] font-semibold tracking-[0.18em] text-slate-500">CATEGORIES</p>
          <div className="space-y-0.5">
            {cats.map((c) => {
              const Icon = getCategoryIcon(c.name);
              const pct = c.group_effective > 0 ? Math.round((c.group_actual / c.group_effective) * 100) : 0;
              const color = c.over_budget ? "#ef4444" : c.warning ? "#f97316" : "#64748b";
              return (
                <button
                  key={c.id}
                  data-testid={`sidebar-cat-${c.id}`}
                  onClick={() => goToCategory(c.id)}
                  className="w-full flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-sm text-slate-400 hover:text-slate-100 hover:bg-white/5 transition-colors"
                >
                  <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: c.color }} />
                  <span className="truncate flex-1 text-left">{c.name}</span>
                  <span className="text-[11px] font-mono-num" style={{ color }}>{pct}%</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="p-3 border-t border-[#1c2537] shrink-0">
        <div className="flex items-center justify-between px-1 mb-2">
          <p className="text-[10px] font-semibold tracking-[0.18em] text-slate-500">LOGGED IN AS</p>
          <UserSettingsDialog />
        </div>
        <div data-testid="user-switcher-toggle" className="flex items-center gap-1 p-1 rounded-xl bg-[#0a0e14] border border-[#2a364f]">
          {users.map((u, i) => {
            const active = u === activeUser;
            const accent = i === 0 ? "#f97316" : "#22c55e";
            return (
              <button
                key={u}
                data-testid={`user-option-${u}`}
                onClick={() => switchUser(u)}
                className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-sm font-medium transition-all"
                style={{ color: active ? "#fff" : "#94a3b8", background: active ? accent : "transparent", boxShadow: active ? `0 0 14px ${accent}55` : "none" }}
              >
                <span className="w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold" style={{ background: active ? "rgba(255,255,255,0.25)" : accent + "33", color: active ? "#fff" : accent }}>
                  {u[0]}
                </span>
                {u}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const TopBar = ({ onOpenMobile }) => {
  const { month, setMonth } = useApp();
  const qc = useQueryClient();
  const isLive = month === currentMonth();
  const { data: cats = [] } = useQuery({ queryKey: ["categories"], queryFn: api.getCategories });

  useEffect(() => {
    api.applyRecurring(month).then((res) => { if (res?.created > 0) qc.invalidateQueries(); });
  }, [month, qc]);

  const rolloverMut = useMutation({
    mutationFn: () => api.rollover(month),
    onSuccess: (res) => {
      toast.success(`Rolled over to ${monthLabel(res.to_month)}`, { description: `${fmt(res.carryover_total)} carried across ${res.categories} categories.` });
      qc.invalidateQueries();
      qc.refetchQueries({ queryKey: ["rollover-log"] });
    },
    onError: () => toast.error("Rollover failed"),
  });

  return (
    <TooltipProvider delayDuration={200}>
      <header className="sticky top-0 z-30 h-16 flex items-center justify-between gap-3 px-4 sm:px-6 backdrop-blur-xl bg-[#0a0e14]/85 border-b border-[#1c2537]">
        <Button variant="ghost" size="icon" className="lg:hidden text-slate-300" onClick={onOpenMobile} data-testid="mobile-menu-button">
          <Menu className="w-5 h-5" />
        </Button>
        <div className="flex-1" />

        <div className="flex items-center gap-1 rounded-full bg-[#141a26] border border-[#2a364f] p-1">
          <Button variant="ghost" size="icon" data-testid="prev-month-button" className="h-7 w-7 text-slate-400 hover:text-slate-100" onClick={() => setMonth(shiftMonth(month, -1))}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span data-testid="month-selector-dropdown" className="px-3 text-sm font-medium text-[#f97316] font-heading min-w-[120px] text-center">{monthLabel(month)}</span>
          <Button variant="ghost" size="icon" data-testid="next-month-button" className="h-7 w-7 text-slate-400 hover:text-slate-100" onClick={() => setMonth(shiftMonth(month, 1))}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#2a364f]">
          <span className={`w-1.5 h-1.5 rounded-full ${isLive ? "bg-[#22c55e] shadow-[0_0_8px_#22c55e]" : "bg-slate-500"}`} />
          <span className="text-xs text-slate-400">{isLive ? "Live" : "Past"}</span>
        </div>

        <div className="flex items-center gap-1">
          <TransactionModal
            cats={cats}
            trigger={
              <Button data-testid="quick-add-button" className="bg-[#f97316] hover:bg-[#ea580c] text-white gap-1.5 h-9 px-3 mr-1">
                <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Add expense</span>
              </Button>
            }
          />
          <RolloverHistory />
          <Tooltip>
            <TooltipTrigger asChild>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="month-rollover-button" className="h-9 w-9 text-slate-400 hover:text-[#f97316] hover:bg-[#f97316]/10">
                    <ArrowRightLeft className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-[#141a26] border-[#2a364f] text-slate-100">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-heading">Roll unspent budget forward?</AlertDialogTitle>
                    <AlertDialogDescription className="text-slate-400">
                      Each category's remaining balance from {monthLabel(month)} carries into the next month as an adjustment. Re-runnable anytime.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-transparent border-[#2a364f] text-slate-300 hover:bg-[#2a364f]">Cancel</AlertDialogCancel>
                    <AlertDialogAction data-testid="confirm-rollover-button" onClick={() => rolloverMut.mutate()} className="bg-[#22c55e] hover:bg-[#16a34a] text-white">Confirm rollover</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </TooltipTrigger>
            <TooltipContent className="bg-[#141a26] border-[#2a364f] text-slate-200">Roll over month</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="refresh-button" className="h-9 w-9 text-slate-400 hover:text-[#22c55e] hover:bg-[#22c55e]/10" onClick={() => { qc.invalidateQueries(); toast.success("Refreshed"); }}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="bg-[#141a26] border-[#2a364f] text-slate-200">Refresh data</TooltipContent>
          </Tooltip>
        </div>
      </header>
    </TooltipProvider>
  );
};

const RolloverHistory = () => {
  const { data: log = [] } = useQuery({ queryKey: ["rollover-log"], queryFn: api.rolloverLog });
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" data-testid="rollover-history-button" className="h-9 w-9 text-slate-400 hover:text-slate-100 hover:bg-white/5">
          <History className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#141a26] border-[#2a364f] text-slate-100 max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><History className="w-5 h-5 text-[#f97316]" /> Rollover history</DialogTitle>
          <DialogDescription className="text-slate-400">Every month rollover and the balance carried forward.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-3 pr-1">
          {log.length === 0 ? (
            <div className="py-10 text-center text-slate-500 text-sm">No rollovers yet. Use the roll-over button to carry unspent budget forward.</div>
          ) : (
            log.map((entry) => (
              <div key={entry.month} data-testid={`rollover-entry-${entry.month}`} className="rounded-xl bg-[#0a0e14] border border-[#2a364f] p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-heading font-semibold">{monthLabel(entry.rolled_from)} <span className="text-slate-500">→</span> {monthLabel(entry.month)}</div>
                  <span className="font-mono-num font-bold" style={{ color: entry.carryover_total >= 0 ? "#22c55e" : "#ef4444" }}>{entry.carryover_total >= 0 ? "+" : ""}{fmt(entry.carryover_total)}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {entry.breakdown?.slice(0, 8).map((b) => (
                    <span key={b.name} className="text-[11px] px-2 py-0.5 rounded-full border border-[#2a364f]" style={{ color: b.color || "#94a3b8" }}>{b.name} {b.amount >= 0 ? "+" : ""}{fmt(b.amount)}</span>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const Layout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  useLocation();
  return (
    <div className="min-h-screen bg-[#0a0e14] text-slate-100">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-[#0c111b] border-r border-[#1c2537] z-40">
        <SidebarContent />
      </aside>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-64 p-0 bg-[#0c111b] border-[#1c2537]">
          <SidebarContent onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="lg:pl-64">
        <TopBar onOpenMobile={() => setMobileOpen(true)} />
        <main className="px-4 sm:px-6 lg:px-8 py-6 grid-noise min-h-[calc(100vh-4rem)]">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
